import { useState } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  Box,
  Flex,
  Heading,
  Text,
  VStack,
  HStack,
  Select,
  useColorModeValue,
  useColorMode,
  Spinner,
  Icon,
} from '@chakra-ui/react';
import { FaHistory } from 'react-icons/fa';
import { useActivityLogs, TimelineFilter, ActivityTypeFilter } from '../useActivityLogs';
import { UserActivityLog } from '../../types';
import { getActivityIcon, getActivityColor, formatTimestamp } from '../activityLogs.util';
import { Card } from '../../../../components/Card';

interface ActivityLogModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function ActivityLogModal({ isOpen, onClose }: ActivityLogModalProps) {
  const [selectedUserId, setSelectedUserId] = useState<string>('');
  const [timelineFilter, setTimelineFilter] = useState<TimelineFilter>('7d');
  const [activityTypeFilter, setActivityTypeFilter] = useState<ActivityTypeFilter>('all');

  const { activityLogs, users, loading } = useActivityLogs(
    selectedUserId || null,
    timelineFilter,
    activityTypeFilter
  );

  const { colorMode } = useColorMode();
  const headingColor = useColorModeValue('gray.900', 'white');
  const subheadingColor = useColorModeValue('gray.600', 'gray.400');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const scrollbarTrackBg = useColorModeValue('gray.100', 'gray.800');
  const scrollbarThumbBg = useColorModeValue('gray.400', 'gray.600');
  
  // Helper function to get color mode value without hooks
  const getColorModeValue = (light: string, dark: string) => {
    return colorMode === 'light' ? light : dark;
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl" isCentered>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <Flex align="center" gap={3}>
            <Icon as={FaHistory} color="blue.600" boxSize={6} />
            <Heading size="md" color={headingColor}>
              User Activity Log
            </Heading>
          </Flex>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody pb={6}>
          <VStack spacing={4} align="stretch">
            {/* Filters */}
            <Flex gap={3} wrap="wrap">
              <Box flex="1" minW="150px">
                <Text fontSize="sm" mb={1} color={subheadingColor}>
                  User
                </Text>
                <Select
                  value={selectedUserId}
                  onChange={(e) => setSelectedUserId(e.target.value)}
                  size="sm"
                >
                  <option value="">All Users</option>
                  {users.map((user) => (
                    <option key={user.id} value={user.id}>
                      {user.name || user.firstName || 'Unknown'}
                    </option>
                  ))}
                </Select>
              </Box>

              <Box flex="1" minW="150px">
                <Text fontSize="sm" mb={1} color={subheadingColor}>
                  Timeline
                </Text>
                <Select
                  value={timelineFilter}
                  onChange={(e) => setTimelineFilter(e.target.value as TimelineFilter)}
                  size="sm"
                >
                  <option value="24h">Last 24 Hours</option>
                  <option value="7d">Last 7 Days</option>
                  <option value="30d">Last 30 Days</option>
                  <option value="90d">Last 90 Days</option>
                  <option value="all">All Time</option>
                </Select>
              </Box>

              <Box flex="1" minW="150px">
                <Text fontSize="sm" mb={1} color={subheadingColor}>
                  Activity Type
                </Text>
                <Select
                  value={activityTypeFilter}
                  onChange={(e) => setActivityTypeFilter(e.target.value as ActivityTypeFilter)}
                  size="sm"
                >
                  <option value="all">All Activities</option>
                  <option value="rock">Rocks</option>
                  <option value="issue">Issues</option>
                  <option value="action_item">Action Items</option>
                  <option value="metric">Metrics</option>
                  <option value="wins_excited">Wins & Excited</option>
                </Select>
              </Box>
            </Flex>

            {/* Activity Count */}
            <Text fontSize="sm" color={subheadingColor}>
              Showing {activityLogs.length} activit{activityLogs.length !== 1 ? 'ies' : 'y'}
            </Text>

            {/* Activity Logs List */}
            <Box
              maxH="500px"
              overflowY="auto"
              pr={2}
              css={{
                '&::-webkit-scrollbar': {
                  width: '8px',
                },
                '&::-webkit-scrollbar-track': {
                  background: scrollbarTrackBg,
                  borderRadius: '4px',
                },
                '&::-webkit-scrollbar-thumb': {
                  background: scrollbarThumbBg,
                  borderRadius: '4px',
                },
              }}
            >
              {loading ? (
                <Flex justify="center" align="center" py={8}>
                  <Spinner size="lg" />
                </Flex>
              ) : activityLogs.length === 0 ? (
                <Card>
                  <VStack spacing={3} py={8}>
                    <Icon as={FaHistory} boxSize={12} color="gray.400" />
                    <Text color={subheadingColor}>No activity found</Text>
                  </VStack>
                </Card>
              ) : (
                <VStack spacing={2} align="stretch">
                  {activityLogs.map((log: UserActivityLog) => {
                    const colors = getActivityColor(log.activityType);
                    const icon = getActivityIcon(log.activityType);
                    const timestamp = log.createdAt
                      ? formatTimestamp(log.createdAt)
                      : 'Unknown time';
                    
                    // Find user for this log
                    const user = users.find((u) => u.id === log.userId);
                    const userName = user?.name || user?.firstName || 'Unknown User';

                    return (
                      <Card
                        key={log.id}
                        bg={getColorModeValue(colors.bgColor, 'gray.700')}
                        borderLeft="4px solid"
                        borderColor={getColorModeValue(colors.borderColor, `${colors.colorScheme}.400`)}
                        py={3}
                        px={4}
                      >
                        <Flex align="start" gap={3}>
                          <Text fontSize="2xl">{icon}</Text>
                          <Box flex="1">
                            <Text fontSize="sm" fontWeight="medium" color={headingColor} mb={1}>
                              {log.activityDescription}
                            </Text>
                            <HStack spacing={3} mt={1}>
                              <Text fontSize="xs" color={subheadingColor}>
                                {userName}
                              </Text>
                              <Text fontSize="xs" color={subheadingColor}>
                                •
                              </Text>
                              <Text fontSize="xs" color={subheadingColor}>
                                {timestamp}
                              </Text>
                            </HStack>
                          </Box>
                        </Flex>
                      </Card>
                    );
                  })}
                </VStack>
              )}
            </Box>
          </VStack>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}

